/**
 * TypeBox schemas for Plan entity — expedition planning container.
 *
 * Replaces the old "Trip" entity. Includes logistics, timing,
 * decision framework, objective hazards, conditions, and pack list.
 */

import { Type, type Static } from 'typebox'
import { UUID, ISODate, Bounds, type Exact } from './common.js'
import { OriginSchema } from './origin.js'

// --- Location ---

export const PlanLocationSchema = Type.Object({
  name: Type.String({ maxLength: 200 }),
  coordinates: Type.Optional(Type.Tuple([Type.Number(), Type.Number()])),
  bounds: Type.Optional(Bounds),
  icon: Type.Optional(Type.String({ maxLength: 50 })),
})
export type PlanLocation = Exact<Static<typeof PlanLocationSchema>>

// --- Times ---

export const PlanTimeTagSchema = Type.Union([
  Type.Literal('meeting'),
  Type.Literal('start'),
  Type.Literal('turnaround'),
  Type.Literal('return'),
  Type.Literal('overdue'),
  Type.Literal('custom'),
])
export type PlanTimeTag = Exact<Static<typeof PlanTimeTagSchema>>

export const PlanTimeSchema = Type.Object({
  id: UUID,
  name: Type.String({ minLength: 1, maxLength: 100 }),
  datetime: ISODate,
  tag: PlanTimeTagSchema,
  location: Type.Optional(PlanLocationSchema),
  note: Type.Optional(Type.String({ maxLength: 2000 })),
})
export type PlanTime = Exact<Static<typeof PlanTimeSchema>>

// --- Decision Framework ---

export const WhenWillWeKnowSchema = Type.Union([
  Type.Literal('continuous'),
  Type.Object({
    type: Type.Literal('datetime'),
    value: ISODate,
  }),
])

export const DecisionFactorSchema = Type.Object({
  id: UUID,
  name: Type.String({ minLength: 1, maxLength: 100 }),
  whenWillWeKnow: WhenWillWeKnowSchema,
  green: Type.String({ maxLength: 500 }),
  yellow: Type.String({ maxLength: 500 }),
  red: Type.String({ maxLength: 500 }),
})
export type DecisionFactor = Exact<Static<typeof DecisionFactorSchema>>

// --- Pack List ---

export const PlanPackItemSchema = Type.Object({
  id: UUID,
  name: Type.String({ minLength: 1, maxLength: 100 }),
  description: Type.Optional(Type.String({ maxLength: 500 })),
  quantity: Type.Optional(Type.Number({ minimum: 1 })),
  packed: Type.Boolean(),
  assignedTo: Type.Optional(UUID),
  group: Type.Optional(Type.String({ maxLength: 100 })),
})
export type PlanPackItem = Exact<Static<typeof PlanPackItemSchema>>

// --- Emergency Contacts ---

export const PlanEmergencyContactSourceSchema = Type.Union([
  Type.Literal('custom'),
  Type.Object({ type: Type.Literal('team'), teamId: UUID, teamName: Type.String() }),
  Type.Object({ type: Type.Literal('member'), userId: UUID, displayName: Type.String() }),
])
export type PlanEmergencyContactSource = Exact<Static<typeof PlanEmergencyContactSourceSchema>>

export const PlanEmergencyContactSchema = Type.Object({
  id: UUID,
  name: Type.String({ minLength: 1, maxLength: 100 }),
  phone: Type.String({ minLength: 1, maxLength: 20 }),
  relationship: Type.Optional(Type.String({ maxLength: 50 })),
  source: PlanEmergencyContactSourceSchema,
})
export type PlanEmergencyContact = Exact<Static<typeof PlanEmergencyContactSchema>>

// --- Rich Text ---

export const RichTextSchema = Type.Object({
  type: Type.Literal('doc'),
  content: Type.Optional(Type.Array(Type.Unknown())),
})
export type RichText = Exact<Static<typeof RichTextSchema>>

// --- Plan ---

export const PlanSchema = Type.Object({
  id: UUID,
  name: Type.String({ minLength: 1, maxLength: 100 }),
  description: Type.Optional(Type.String({ maxLength: 2000 })),
  descriptionRich: Type.Optional(RichTextSchema),
  location: Type.Optional(PlanLocationSchema),

  // Times (flexible list — replaces fixed startDate/endDate/meetingTime/etc.)
  times: Type.Array(PlanTimeSchema),

  // Decision Framework
  decisionFactors: Type.Array(DecisionFactorSchema),
  decisionRules: Type.Array(Type.String({ maxLength: 200 })),
  objectiveHazards: Type.Optional(Type.String({ maxLength: 5000 })),
  objectiveHazardsRich: Type.Optional(RichTextSchema),

  // Conditions (future: auto-populated)
  weatherForecast: Type.Optional(Type.String({ maxLength: 5000 })),
  weatherCurrent: Type.Optional(Type.String({ maxLength: 5000 })),
  avyForecast: Type.Optional(Type.String({ maxLength: 5000 })),

  // Emergency contacts
  emergencyContacts: Type.Array(PlanEmergencyContactSchema),

  // Pack list
  teamGear: Type.Array(PlanPackItemSchema),
  individualGear: Type.Array(PlanPackItemSchema),

  // Spatial
  bounds: Type.Optional(Bounds),

  // Provenance
  createdFrom: Type.Optional(
    Type.Object({
      type: Type.Union([Type.Literal('route'), Type.Literal('activity')]),
      id: UUID,
    })
  ),
  origin: OriginSchema,
  createdBy: UUID,
  version: Type.Number({ minimum: 0 }),
  createdAt: ISODate,
  updatedAt: ISODate,
})
export type Plan = Exact<Static<typeof PlanSchema>>

/** Fields required when creating a plan */
export const PlanCreateSchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 100 }),
  origin: OriginSchema,
  createdBy: UUID,
})

/** Partial plan for updates */
export const PlanUpdateSchema = Type.Partial(PlanSchema)
